export const METER_META = {
  safety: {
    label: "Safety",
    icon: "ShieldCheck",
    bar: "bg-emerald-600",
    text: "text-emerald-700",
    ring: "ring-emerald-600/20",
    hint: "Immediate risk and protection",
  },
  trust: {
    label: "Trust",
    icon: "HeartHandshake",
    bar: "bg-sky-600",
    text: "text-sky-700",
    ring: "ring-sky-600/20",
    hint: "Rapport and the working alliance",
  },
  time: {
    label: "Time",
    icon: "Clock",
    bar: "bg-amber-500",
    text: "text-amber-700",
    ring: "ring-amber-500/20",
    hint: "Capacity across the shift",
  },
  resources: {
    label: "Resources",
    icon: "LifeBuoy",
    bar: "bg-indigo-600",
    text: "text-indigo-700",
    ring: "ring-indigo-600/20",
    hint: "Referrals, evidence, options",
  },
};

export const TONE_STYLES = {
  good: {
    label: "Survivor-centered",
    panel: "bg-emerald-50 border-emerald-200 text-emerald-900",
    dot: "bg-emerald-600",
    badge: "bg-emerald-100 text-emerald-800",
    icon: "CircleCheck",
  },
  caution: {
    label: "Mixed",
    panel: "bg-amber-50 border-amber-200 text-amber-900",
    dot: "bg-amber-500",
    badge: "bg-amber-100 text-amber-800",
    icon: "TriangleAlert",
  },
  harmful: {
    label: "Harmful",
    panel: "bg-rose-50 border-rose-200 text-rose-900",
    dot: "bg-rose-500",
    badge: "bg-rose-100 text-rose-800",
    icon: "CircleX",
  },
};

export const OUTCOME_TIERS = {
  advocate: {
    title: "Survivor-Centered Advocate",
    blurb:
      "You consistently protected safety and autonomy while building genuine trust. Your client left with a real plan and a person they can reach.",
    tone: "good",
  },
  solid: {
    title: "Solid Foundation",
    blurb:
      "Strong instincts and mostly survivor-led choices, with a few moments to refine. Your client felt supported and safer than when the case began.",
    tone: "good",
  },
  developing: {
    title: "Developing Practice",
    blurb:
      "Some choices supported your client, others added risk, eroded trust, or ran the clock down. Review the debrief to tighten your survivor-centered instincts.",
    tone: "caution",
  },
  reflect: {
    title: "Needs Reflection",
    blurb:
      "Several decisions removed your client's agency or raised their risk. That's exactly what training is for; read the coach's debrief closely.",
    tone: "harmful",
  },
  crisis: {
    title: "Case Ended in Crisis",
    blurb:
      "Safety collapsed before the case could be completed; your client disengaged or the situation escalated. This is the hardest lesson: in advocacy, protecting safety and trust has to come first, every time.",
    tone: "harmful",
  },
};

export function computeTier(meters, harmfulCount, endedEarly = false) {
  if (endedEarly || meters.safety <= 12) return "crisis";

  const score =
    meters.safety * 0.4 +
    meters.trust * 0.35 +
    meters.resources * 0.15 +
    meters.time * 0.1;

  const key = baseTier(score, harmfulCount, meters.safety);
  return applyTimePenalty(key, meters.time);
}

function baseTier(score, harmfulCount, safety) {
  if (harmfulCount >= 3 || safety < 35) return "reflect";
  if (score >= 78 && harmfulCount === 0) return "advocate";
  if (score >= 62) return "solid";
  return "developing";
}

// Timing matters: running the clock down caps how well the case can land.
function applyTimePenalty(key, time) {
  if (time < 12 && (key === "advocate" || key === "solid")) return "developing";
  if (time < 22 && key === "advocate") return "solid";
  return key;
}

export const clampMeter = (v) => Math.max(0, Math.min(100, v));

export const PRINCIPLES = [
  { key: "safety", label: "Safety", desc: "Physical and emotional safety come first, for the survivor and anyone with her." },
  { key: "trustworthiness", label: "Trustworthiness", desc: "Honesty about limits, predictable follow-through, no promises you can't keep." },
  { key: "choice", label: "Choice", desc: "The survivor decides; options are offered, never mandated." },
  { key: "collaboration", label: "Collaboration", desc: "Plans are built with the survivor, and with the team around her." },
  { key: "empowerment", label: "Empowerment", desc: "Name strengths, counter self-blame, and reflect her agency back to her." },
];

// Applies consequence threading: flags set by earlier choices can extend the
// situation text and modify a later choice's effects/feedback.
export function applyThreading(dec, activeFlags) {
  if (!dec) return dec;
  let situation = dec.situation;
  (dec.threads || []).forEach((t) => {
    if (activeFlags[t.flag]) situation = `${situation} ${t.append}`;
  });
  const choices = dec.choices.map((c) => applyChoiceModifiers(c, activeFlags));
  return { ...dec, situation, choices };
}

function applyChoiceModifiers(choice, activeFlags) {
  let effects = choice.effects;
  let feedback = choice.feedback;
  (choice.modifiers || []).forEach((m) => {
    if (!activeFlags[m.flag]) return;
    effects = addEffects(effects, m.effects);
    if (m.feedback_append) feedback = `${feedback} ${m.feedback_append}`;
  });
  return { ...choice, effects, feedback };
}

function addEffects(base, extra = {}) {
  return {
    safety: base.safety + (extra.safety || 0),
    trust: base.trust + (extra.trust || 0),
    time: base.time + (extra.time || 0),
    resources: base.resources + (extra.resources || 0),
  };
}

